import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { endpoints } from '@/config/apiEndpoints';
import { useActiveOrganizationId } from '@/features/auth/session';
import { useLiveCollection, type LiveCollection } from '@/hooks/useLiveCollection';
import { apiClient } from '@/lib/apiClient';
import { collections } from '@/lib/firebase';
import { parseCampaign, type Campaign, type CampaignFormValues, type CampaignStatus } from './types';

const CAMPAIGN_LIMIT = 200;

export const campaignsQueryKey = ['campaigns'] as const;

export function useCampaigns(): LiveCollection<Campaign> {
  const organizationId = useActiveOrganizationId();

  return useLiveCollection<Campaign>({
    path: collections.campaigns(organizationId ?? '_'),
    httpPath: endpoints.campaigns.list(),
    queryKey: [...campaignsQueryKey, organizationId],
    parse: parseCampaign,
    limit: CAMPAIGN_LIMIT,
    orderByField: 'updatedAt',
    enabled: Boolean(organizationId),
  });
}

export function useCampaign(campaignId: string | undefined): {
  campaign: Campaign | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { data, loading, error, refetch } = useCampaigns();
  const campaign = useMemo(
    () => (campaignId ? data.find((item) => item.id === campaignId) ?? null : null),
    [data, campaignId],
  );
  return { campaign, loading, error, refetch };
}

export function useFilteredCampaigns(
  campaigns: Campaign[],
  query: string,
  status: CampaignStatus | 'all',
): Campaign[] {
  return useMemo(() => {
    const needle = query.trim().toLowerCase();
    return campaigns.filter((campaign) => {
      if (status !== 'all' && campaign.status !== status) return false;
      if (!needle) return true;
      return (
        campaign.name.toLowerCase().includes(needle) ||
        campaign.brand.toLowerCase().includes(needle)
      );
    });
  }, [campaigns, query, status]);
}

export function useCreateCampaign() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: CampaignFormValues) =>
      apiClient.post<{ id: string }>(endpoints.campaigns.create(), values),
    onSuccess: () => client.invalidateQueries({ queryKey: campaignsQueryKey }),
  });
}

export function useUpdateCampaign(campaignId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (values: CampaignFormValues) =>
      apiClient.put<{ id: string }>(endpoints.campaigns.update(campaignId), values),
    onSuccess: () => client.invalidateQueries({ queryKey: campaignsQueryKey }),
  });
}

export function useDeactivateCampaign() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) =>
      apiClient.post<void>(endpoints.campaigns.deactivate(campaignId)),
    onSuccess: () => client.invalidateQueries({ queryKey: campaignsQueryKey }),
  });
}
